import { FormEvent, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import GoogleSignInButton from "../../components/GoogleSignInButton";
import LegalAcceptance from "../../components/LegalAcceptance";
import AppTextField from "../../components/form/AppTextField";
import { AuthPageShell } from "../../components/layout";
import sessionAuthApi, { type AuthProviders } from "../../services/sessionAuth";
import { startGoogleLogin } from "../../services/googleOAuth";
import { authEntryButtonSx } from "../login/authEntryButtonSx";

/**
 * Google sign-up only asks for the workspace name in our UI, then sends the browser
 * straight to Google via Keycloak broker (kc_idp_hint) — never Keycloak registration.
 */
export default function RegisterGoogleUI() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const invite = params.get("invite");
  const [workspaceName, setWorkspaceName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [legalError, setLegalError] = useState(false);
  const [providers, setProviders] = useState<AuthProviders | null>(null);

  useEffect(() => {
    sessionAuthApi.getAuthProviders().then(setProviders).catch(() => setProviders(null));
  }, []);

  const start = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!providers?.googleEnabled) {
      setError(t("auth.registerGoogle.unavailable"));
      return;
    }
    if (!acceptedLegal) {
      setLegalError(true);
      setError(t("auth.register.legal.required"));
      return;
    }
    if (!invite) {
      const trimmed = workspaceName.trim();
      if (trimmed.length < 2 || trimmed.length > 80) {
        setError(t("auth.register.validation.workspaceNameLength"));
        return;
      }
    }
    setError(null);
    setLegalError(false);
    setLoading(true);
    try {
      await startGoogleLogin(providers, {
        invite,
        workspaceName: invite ? null : workspaceName.trim(),
      });
    } catch {
      setLoading(false);
      setError(t("auth.registerGoogle.unavailable"));
    }
  };

  return (
    <AuthPageShell
      maxWidth={480}
      title={t("auth.registerGoogle.title")}
      subtitle={t("auth.registerGoogle.subtitle")}
      footer={
        <Link to={invite ? `/register?invite=${encodeURIComponent(invite)}` : "/register"}>
          {t("auth.registerGoogle.backToRegister")}
        </Link>
      }
    >
      <Box component="form" noValidate onSubmit={start}>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {!invite && (
            <AppTextField
              label={t("auth.register.workspaceName")}
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              required
              autoFocus
            />
          )}
          <LegalAcceptance
            checked={acceptedLegal}
            error={legalError}
            helperText={legalError ? t("auth.register.legal.required") : null}
            onChange={(value) => {
              setAcceptedLegal(value);
              if (value) {
                setLegalError(false);
                setError(null);
              }
            }}
          />
          <GoogleSignInButton
            onClick={() => start()}
            label={t("auth.registerGoogle.continue")}
            disabled={loading || !providers?.googleEnabled}
            sx={authEntryButtonSx}
          />
        </Stack>
      </Box>
    </AuthPageShell>
  );
}
